import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
  inject
} from "@angular/core";
import { GridComponent } from "@app/view/shared/base-pages/base-data-table-page/grid.component";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
  Validators
} from "@angular/forms";
import { ResourceStore } from "@app/services/store/resource-store";
import {
  MatStep,
  MatStepContent,
  MatStepLabel,
  MatStepper
} from "@angular/material/stepper";
import { Resource_DetailProjection } from "@app/models/rest/resource";
import {
  MatDialogActions,
  MatDialogContent,
  MatDialogRef
} from "@angular/material/dialog";
import { MatSelect, MatSelectChange } from "@angular/material/select";
import { AvailabilityCodeStore } from "@app/services/store/availability-code-store";
import { AvailabilityVisitService } from "@app/services/http/availability-visit.service";
import { AvailabilityCode } from "@app/models/rest/availability-code";
import { AvailabilityType } from "@app/models/enums/availability-type.enum";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { ShiftPatternEntry } from "@app/services/http/availability-service/shift-pattern.entry";
import { AvailabilityBulkRequest } from "@app/services/http/availability-service/availability-bulk.request";
import moment from "moment";
import { NgxUiLoaderModule, NgxUiLoaderService } from "ngx-ui-loader";
import { Store } from "@bryntum/core-thin";
import {
  DateColumnConfig,
  RowExpanderConfig,
  TemplateColumnConfig
} from "@bryntum/grid-thin";
import { BulkAvailabilityChange } from "@app/services/http/availability-service/bulk-availability.change";
import { DetectedConflict } from "@app/services/http/availability-service/detected.conflict";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { MatButton } from "@angular/material/button";
import { GridComponent as GridComponent_1 } from "../../shared/base-pages/base-data-table-page/grid.component";
import {
  MatFormField,
  MatLabel,
  MatSuffix
} from "@angular/material/form-field";
import { MatOption } from "@angular/material/core";
import { MatInput } from "@angular/material/input";
import { MatSlider, MatSliderThumb } from "@angular/material/slider";
import { MatCheckbox } from "@angular/material/checkbox";
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle
} from "@angular/material/datepicker";
import { TranslatePipe } from "@ngx-translate/core";
import { DialogHeaderComponent } from "@app/view/shared/dialog-header/dialog-header.component";

@Component({
  selector: "app-bulk-availability-wizard",
  templateUrl: "./bulk-availability-wizard.component.html",
  styleUrls: ["./bulk-availability-wizard.component.css"],
  imports: [
    MatDialogContent,
    NgxUiLoaderModule,
    MatStepper,
    MatStep,
    MatStepLabel,
    MatStepContent,
    GridComponent_1,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    MatSlider,
    MatSliderThumb,
    MatCheckbox,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatDialogActions,
    MatButton,
    TranslatePipe,
    DialogHeaderComponent
  ]
})
export class BulkAvailabilityWizardComponent implements OnInit, AfterViewInit {
  private readonly weekdayNames = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday"
  ] as const;

  dialogRef =
    inject<MatDialogRef<BulkAvailabilityWizardComponent>>(MatDialogRef);
  ngxLoading = inject(NgxUiLoaderService);
  private resourceStore = inject(ResourceStore);
  protected clientConfig = inject(ClientConfigService);
  availabilityCodeStore = inject(AvailabilityCodeStore);
  serviceErrorHandler = inject(ServiceErrorHandlerService);
  availabilityVisitStore = inject(AvailabilityVisitService);

  resourceFormGroup: FormGroup = new FormGroup({});
  typeFormGroup: FormGroup = new FormGroup<{
    availabilityCode: FormControl<AvailabilityCode>;
    note: FormControl<string>;
  }>({
    availabilityCode: new FormControl<AvailabilityCode>(null),
    note: new FormControl<string>(null)
  });

  timingFormGroup: FormGroup = new FormGroup<{
    startDate: FormControl<Date>;
    endDate: FormControl<Date>;
    startTime: FormControl<string>;
    endTime: FormControl<string>;
    monday: FormControl<boolean>;
    tuesday: FormControl<boolean>;
    wednesday: FormControl<boolean>;
    thursday: FormControl<boolean>;
    friday: FormControl<boolean>;
    saturday: FormControl<boolean>;
    sunday: FormControl<boolean>;
  }>({
    startDate: new FormControl<Date>(null, [Validators.required]),
    endDate: new FormControl<Date>(null, [Validators.required]),
    startTime: new FormControl<string>("08:30", [Validators.required]),
    endTime: new FormControl<string>("17:00", [Validators.required]),
    monday: new FormControl<boolean>(true),
    tuesday: new FormControl<boolean>(true),
    wednesday: new FormControl<boolean>(true),
    thursday: new FormControl<boolean>(true),
    friday: new FormControl<boolean>(true),
    saturday: new FormControl<boolean>(false),
    sunday: new FormControl<boolean>(false)
  });
  conflictFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  confirmFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  selectionMode = {
    checkbox: {
      headerRenderer: ({ headerElement }) => {
        headerElement.style.backgroundColor = "white";
        headerElement.style.color = "black";
        return "Find by Name";
      }
    },
    rowCheckboxSelection: true,
    multiSelect: true,
    showCheckAll: true,
    row: true,
    sort: false
  };

  public rowConfig: Partial<RowExpanderConfig> = {
    columnPosition: "last",
    column: {
      renderer: (data) => {
        const { record } = data;
        if (
          !record.get("detectedConflictList") ||
          record.get("detectedConflictList").length === 0
        )
          data.cellElement.classList.add("hide");
        data.cellElement.classList.add("hide");
      }
    },

    renderer: async ({ record }) => {
      let result = "<div>";
      let conflicts: DetectedConflict[] = record.get("detectedConflictList");
      if (conflicts.length === 0) {
        return "<div>No conflicts.</div>";
      }
      if (conflicts.length === 1) {
        for (const value of conflicts) {
          if (value.despatchedTicketIds.length > 0) {
            return "<div>This shift has despatched ticket, and therefore this tool cannot be used on this date.</div>";
          }
        }
        return "<div>This resource currently has an availability record on this date.  By continuing, this existing availability record will be replaced, and any locked tickets will be unlocked.</div>";
      }

      return (
        "<div>This resource currently has " +
        conflicts.length +
        " availability records on this date.  By continuing, the existing availability records will be replaced, and any locked tickets will be unlocked.</div>"
      );
    }
  };
  @ViewChild("stepper", { static: false }) stepper: MatStepper;

  @ViewChild(GridComponent, { static: false })
  public resourceGrid: GridComponent;

  @ViewChild(GridComponent, { static: false })
  public conflictGridDisplay: GridComponent;
  @Input()
  public resourceColumns: Partial<TemplateColumnConfig>[] = [
    {
      field: "name",
      text: "Name",
      flex: 1,
      template: ({ record }) => `<dl style="margin:0" >
                 <dt><img alt="Resource Image" src="${record.get(
                   "imageUrl"
                 )}" height="40" width="40" style="border-radius:100%;box-shadow:1px 0 5px #aaa"/></dt>
                 </dl>${record.get("name")}`,
      editor: false,
      sortable: false,
      filterable: {
        filterField: {
          type: "text",
          placeholder: "Find by Name"
        }
      },
      headerRenderer: ({ column, headerElement }) => {
        headerElement.style.backgroundColor = "white";
        headerElement.style.color = "black";
        return "";
      }
    },
    {
      field: "teamName",
      text: "Team",
      flex: 1,
      editor: false,
      sortable: false,
      filterable: {
        filterField: {
          type: "text",
          placeholder: "Find by Team"
        }
      },
      headerRenderer: ({ column, headerElement }) => {
        headerElement.style.backgroundColor = "white";
        headerElement.style.color = "black";
        return "";
      }
    }
  ];
  availabilityCodes: AvailabilityCode[];
  public isAssignType: boolean;
  public isBlockType: boolean;
  bulkResponse: string;
  conflictData: BulkAvailabilityChange[];
  public resources: Resource_DetailProjection[];
  public conflictColumns: Partial<DateColumnConfig>[] = [
    {
      field: "conflicts",
      text: "",
      width: 10,
      htmlEncode: false,
      renderer: ({ record }) =>
        !record.get("detectedConflictList") ||
        record.get("detectedConflictList").length === 0
          ? '<i class="fa fa-circle-check" style="font-size:24px; margin-left:12px;color: green;"></i>'
          : '<i class="fa fa-exclamation-circle" style="font-size:24px;margin-left:12px; color: red;"></i>'
    },
    {
      field: "resourceId",
      text: "Resource",
      sortable: false,
      flex: 1,
      renderer: ({ record }) =>
        this.resources.find((s) => s.id === record.get("resourceId")).name
    },
    {
      field: "startDate",
      text: "Start",
      width: 140,
      type: "date",
      format: "DD/MM/YYYY HH:mm"
    },
    {
      field: "endDate",
      text: "End",
      width: 140,
      type: "date",
      format: "DD/MM/YYYY HH:mm"
    }
  ];
  public errorMessage: string;
  public showError: boolean;
  public feasible: Boolean;
  public cyclicalPriorityReservationPercent: number = 100;
  public normalPriorityReservationPercent: number = 100;
  public highPriorityReservationPercent: number = 100;
  public emergencyPriorityReservationPercent: number = 100;
  private selectedCode: any;

  ngOnInit(): void {
    this.initialLoad();
  }

  public formatLabel(value: number): string {
    return value + "%";
  }

  private initialLoad(): void {
    this.availabilityCodeStore
      .loadAll({ params: { projection: "detail" } }, true)
      .then((availabilityCodes) => {
        this.availabilityCodes = availabilityCodes.resources;
      });

    this.ngxLoading.startLoader("scheduleLoad");
    this.resourceStore
      .loadAll({ params: { projection: "detail" } })
      .then((value) => {
        this.ngxLoading.stopLoader("scheduleLoad");
        console.log("Loaded resources", value);
        this.resources =
          value.resources as unknown as Resource_DetailProjection[];
        this.resources.forEach((resource) => {
          this.resourceGrid.addRow({
            name: resource.name,
            imageUrl: resource.imageUrl,
            zones: resource.zonesList.join(", "),
            skills: resource.skillsList
              .map((value1) => value1.skill.description)
              .join(", "),
            teamName: resource.team?.name,
            id: resource.id,
            selected: false
          });
        });
      });
  }

  public startTimeChanged($event: Event): void {}

  public endTimeChanged($event: Event): void {}

  public stepperBack(): void {
    this.stepper.previous();
  }

  public stepperNext(): void {
    switch (this.stepper.selectedIndex) {
      case 0: {
        if (
          !this.resourceGrid.selectedRecords ||
          this.resourceGrid.selectedRecords.length === 0
        ) {
          this.serviceErrorHandler.handleClientError(
            "Please select at least one resource to proceed."
          );
        } else {
          this.stepper.next();
        }
        break;
      }
      case 1: {
        this.stepper.next();
        break;
      }
      case 2: {
        if (!this.hasSelectedWeekday()) {
          this.serviceErrorHandler.handleClientError(
            "Please select at least one weekday to proceed."
          );
          return;
        }

        const partitionValidationError = this.getPartitionValidationError();
        if (partitionValidationError) {
          this.serviceErrorHandler.handleClientError(partitionValidationError);
          return;
        }

        let selectedDate = moment(this.timingFormGroup.get("endDate")?.value);
        let maxPlanningWindow: number =
          this.clientConfig.config.maxPlanningWindow;
        let todayPlusMaxPlanningWindow = moment().add(
          maxPlanningWindow,
          "days"
        );

        if (
          maxPlanningWindow != 0 &&
          selectedDate.isAfter(todayPlusMaxPlanningWindow) &&
          this.typeFormGroup.controls.availabilityCode.value
            ?.availabilityType !== "BLOCK"
        ) {
          this.serviceErrorHandler.handleClientError(
            `The selected date is beyond ${maxPlanningWindow} days and has an invalid availability code.`
          );
          return;
        } else {
          console.log(
            `The selected date is within ${maxPlanningWindow} days from today/contains a valid code.`
          );
        }
        this.stepper.next();
        break;
      }
      case 3: {
        if (!this.feasible) {
          this.serviceErrorHandler.handleClientError(
            "Please review conflicts, as there is a critical issue preventing this tool from running"
          );
          return;
        }
        this.bulkUpdateCall(true);
      }
    }
  }

  public onCloseClick(): void {
    this.dialogRef.close(false);
  }

  public selectedCodeChange($event: MatSelectChange): void {
    console.log("code change", $event.value);
    this.selectedCode = $event.value;
    this.isAssignType = false;
    this.isBlockType = false;
    if (!this.selectedCode) {
      return;
    }

    switch ($event.value.availabilityType) {
      case "ASSIGN":
        this.isAssignType = true;
        this.isBlockType = false;
        break;
      case "BLOCK":
        this.isAssignType = false;
        this.isBlockType = true;
        break;
    }
  }

  public testIsBlockType(avType: AvailabilityType): boolean {
    return avType === AvailabilityType.BLOCK;
  }

  private getPattern(
    offset: number,
    startHour: number,
    startMinute: number,
    endHour: number,
    endMinute: number
  ): ShiftPatternEntry {
    console.log(
      "Working out pattern for offset",
      offset,
      startHour,
      startMinute,
      endHour,
      endMinute
    );
    let pattern = new ShiftPatternEntry();
    pattern.offset = offset;
    pattern.startHour = startHour;
    pattern.startMinute = startMinute;

    let startMinuteTotal = startHour * 60 + startMinute;
    let endMinuteTotal = endHour * 60 + endMinute;

    let duration = endMinuteTotal - startMinuteTotal;
    console.log("calculated duration", duration);
    if (duration <= 0) {
      duration += 24 * 60;
    }
    pattern.duration = duration;
    return pattern;
  }

  private hasSelectedWeekday(): boolean {
    return this.weekdayNames.some(
      (weekday) => this.timingFormGroup.value[weekday]
    );
  }

  private getPartitionValidationError(): string | null {
    const processedStartTime = moment(
      this.timingFormGroup.value["startTime"],
      "HH:mm"
    );
    const processedEndTime = moment(
      this.timingFormGroup.value["endTime"],
      "HH:mm"
    );
    const startMinuteTotal =
      processedStartTime.hour() * 60 + processedStartTime.minute();
    const endMinuteTotal =
      processedEndTime.hour() * 60 + processedEndTime.minute();
    let duration = endMinuteTotal - startMinuteTotal;

    if (duration <= 0) {
      duration += 24 * 60;
    }

    const endOffsetMinuteTotal = startMinuteTotal + duration;
    const partitioning = this.clientConfig.config.partitioning ?? {
      splitHour: 7,
      splitDays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
        sunday: true
      }
    };
    const boundaryMinute = partitioning.splitHour * 60;

    for (let dayIndex = 0; dayIndex < this.weekdayNames.length; dayIndex++) {
      const weekday = this.weekdayNames[dayIndex];
      if (!this.timingFormGroup.value[weekday]) {
        continue;
      }

      const startDayHasSplit = partitioning.splitDays[weekday] ?? true;
      const nextDay =
        this.weekdayNames[(dayIndex + 1) % this.weekdayNames.length];
      const nextDayHasSplit = partitioning.splitDays[nextDay] ?? true;

      if (
        startDayHasSplit &&
        startMinuteTotal < boundaryMinute &&
        endOffsetMinuteTotal > boundaryMinute
      ) {
        return this.getPartitionErrorMessage(weekday, partitioning.splitHour);
      }

      if (
        endOffsetMinuteTotal > 24 * 60 &&
        nextDayHasSplit &&
        endOffsetMinuteTotal > 24 * 60 + boundaryMinute
      ) {
        return this.getPartitionErrorMessage(nextDay, partitioning.splitHour);
      }
    }

    return null;
  }

  private getPartitionErrorMessage(weekday: string, splitHour: number): string {
    return `The selected time range crosses the ${splitHour.toString().padStart(2, "0")}:00 partition boundary for ${weekday}. Please choose a shift that ends at or before the boundary, or starts after it.`;
  }

  private bulkUpdateCall(commit = false): void {
    console.log("Data:");
    console.log("resources", this.resourceGrid.selectedRecords);
    console.log("typeForm", this.typeFormGroup);
    console.log("timingForm", this.timingFormGroup);
    this.ngxLoading.startLoader("bulkUpdate");
    let request = new AvailabilityBulkRequest();
    request.availabilityCodeId =
      this.typeFormGroup.value["availabilityCode"]?.id;
    request.note = this.typeFormGroup.value["note"];
    request.originDate = moment("2023-01-02T00:00:00").toDate();
    request.startDate = moment(this.timingFormGroup.value["startDate"])
      .utc(true)
      .toDate();
    request.endDate = moment(this.timingFormGroup.value["endDate"])
      .utc(true)
      .toDate();
    request.pattern = [];
    request.length = 7;
    request.conflictResolutions = [];
    request.resources = this.resourceGrid.selectedRecords.map((value) =>
      parseInt(value.id.toString(), 10)
    );
    request.commit = commit;
    request.defaultAction = true;
    request.cyclicalPriorityReservation =
      this.cyclicalPriorityReservationPercent;
    request.normalPriorityReservation = this.normalPriorityReservationPercent;
    request.highPriorityReservation = this.highPriorityReservationPercent;
    request.emergencyPriorityReservation =
      this.emergencyPriorityReservationPercent;

    let processedStartTime = moment(
      this.timingFormGroup.value["startTime"],
      "HH:mm"
    );
    let processedEndTime = moment(
      this.timingFormGroup.value["endTime"],
      "HH:mm"
    );

    if (this.timingFormGroup.value["monday"]) {
      console.log("Generating pattern for Monday");
      request.pattern.push(
        this.getPattern(
          0,
          processedStartTime.hour(),
          processedStartTime.minute(),
          processedEndTime.hour(),
          processedEndTime.minute()
        )
      );
    }
    if (this.timingFormGroup.value["tuesday"]) {
      console.log("Generating pattern for Tuesday");

      request.pattern.push(
        this.getPattern(
          1,
          processedStartTime.hour(),
          processedStartTime.minute(),
          processedEndTime.hour(),
          processedEndTime.minute()
        )
      );
    }
    if (this.timingFormGroup.value["wednesday"]) {
      console.log("Generating pattern for Wednesday");

      request.pattern.push(
        this.getPattern(
          2,
          processedStartTime.hour(),
          processedStartTime.minute(),
          processedEndTime.hour(),
          processedEndTime.minute()
        )
      );
    }
    if (this.timingFormGroup.value["thursday"]) {
      console.log("Generating pattern for Thursday");

      request.pattern.push(
        this.getPattern(
          3,
          processedStartTime.hour(),
          processedStartTime.minute(),
          processedEndTime.hour(),
          processedEndTime.minute()
        )
      );
    }
    if (this.timingFormGroup.value["friday"]) {
      console.log("Generating pattern for Friday");

      request.pattern.push(
        this.getPattern(
          4,
          processedStartTime.hour(),
          processedStartTime.minute(),
          processedEndTime.hour(),
          processedEndTime.minute()
        )
      );
    }
    if (this.timingFormGroup.value["saturday"]) {
      console.log("Generating pattern for Saturday");

      request.pattern.push(
        this.getPattern(
          5,
          processedStartTime.hour(),
          processedStartTime.minute(),
          processedEndTime.hour(),
          processedEndTime.minute()
        )
      );
    }
    if (this.timingFormGroup.value["sunday"]) {
      console.log("Generating pattern for Sunday");

      request.pattern.push(
        this.getPattern(
          6,
          processedStartTime.hour(),
          processedStartTime.minute(),
          processedEndTime.hour(),
          processedEndTime.minute()
        )
      );
    }
    console.log("Final request", request);
    let store = this.conflictGridDisplay.gridEngine.store as Store;
    console.log("Store", store);

    this.availabilityVisitStore.bulk(request).then(
      (value) => {
        console.log("Called", value);
        this.feasible = value.feasible;
        let processed = value.availabilityChangeList.map((value1) => {
          value1.startDate = moment(value1.startDate).toDate();
          value1.endDate = moment(value1.endDate).toDate();
          return value1;
        });
        this.bulkResponse = JSON.stringify(processed);
        this.conflictData = value.availabilityChangeList;

        this.ngxLoading.stopLoader("bulkUpdate");
        if (commit) {
          this.serviceErrorHandler.handleClientInfo(
            "The wizard has finished and completed successfully.",
            "Success"
          );
          this.dialogRef.close({
            success: true,
            message: "The wizard has finished and completed successfully."
          });
        }
      },
      (reason) => {
        this.ngxLoading.stopLoader("bulkUpdate");
        this.errorMessage =
          reason.message !== undefined ? reason.message : reason;
        this.showError = true;
        if (commit) {
          // On commit failure nothing was saved, so surface a prominent
          // dialog (mirroring the success path) rather than relying solely
          // on the inline preview panel, which may be scrolled out of view.
          this.serviceErrorHandler.handleError(reason);
        }
      }
    );
  }

  ngAfterViewInit(): void {
    this.stepper.selectionChange.subscribe((selectionChangeEvent) => {
      console.log("change event", selectionChangeEvent);
      if (selectionChangeEvent.selectedIndex === 3) {
        this.bulkUpdateCall();
      }
    });
  }

  public cyclicalSliderChanged(value: number): void {
    this.cyclicalPriorityReservationPercent = value;
    if (this.normalPriorityReservationPercent < value) {
      this.normalPriorityReservationPercent = value;
    }
    if (this.highPriorityReservationPercent < value) {
      this.highPriorityReservationPercent = value;
    }
    if (this.emergencyPriorityReservationPercent < value) {
      this.emergencyPriorityReservationPercent = value;
    }
  }

  public normalSliderChanged(value: number): void {
    this.normalPriorityReservationPercent = value;
    if (this.highPriorityReservationPercent < value) {
      this.highPriorityReservationPercent = value;
    }
    if (this.emergencyPriorityReservationPercent < value) {
      this.emergencyPriorityReservationPercent = value;
    }
    if (this.cyclicalPriorityReservationPercent > value) {
      this.cyclicalPriorityReservationPercent = value;
    }
  }

  public highSliderChanged(value: number): void {
    this.highPriorityReservationPercent = value;
    if (this.emergencyPriorityReservationPercent < value) {
      this.emergencyPriorityReservationPercent = value;
    }
    if (this.normalPriorityReservationPercent > value) {
      this.normalPriorityReservationPercent = value;
    }
    if (this.cyclicalPriorityReservationPercent > value) {
      this.cyclicalPriorityReservationPercent = value;
    }
  }

  public emergencySliderChanged(value: number): void {
    if (this.highPriorityReservationPercent > value) {
      this.highPriorityReservationPercent = value;
    }
    if (this.normalPriorityReservationPercent > value) {
      this.normalPriorityReservationPercent = value;
    }
    if (this.cyclicalPriorityReservationPercent > value) {
      this.cyclicalPriorityReservationPercent = value;
    }
    this.emergencyPriorityReservationPercent = value;
  }
}
